import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Chip,
  Alert,
  Card,
  CardContent,
  alpha,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Paid as PaidIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/apiService';
import { printReceipt } from '../../../utils/printVoucher';

interface IAccount {
  id: string;
  name: string;
  accountNumber?: string;
}

interface IReceipt {
  id: string;
  receiptNumber: string;
  date: string;
  amount: number;
  debitAccount: IAccount;
  creditAccount: IAccount;
  description: string;
  status: string;
  createdBy: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  customer?: string;
  branchId?: string;
  branchName?: string;
}

interface ReceiptViewProps {
  receiptId: string | null;
  open: boolean;
  onClose: () => void;
}

const ReceiptView: React.FC<ReceiptViewProps> = ({ receiptId, open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [receipt, setReceipt] = useState<IReceipt | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && receiptId) {
      fetchReceipt();
    } else if (!open) {
      // إعادة تعيين الحالة عند إغلاق النافذة
      setReceipt(null);
      setError(null);
    }
  }, [receiptId, open]);

  const fetchReceipt = async () => {
    if (!receiptId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.get<any>(`/finance/receipts/${receiptId}`);
      
      // معالجة الاستجابة
      let receiptData: any;
      
      if (response.data) {
        receiptData = response.data;
      } else if (response.receipt) {
        receiptData = response.receipt;
      } else {
        receiptData = response;
      }
      
      // تنسيق البيانات
      const formattedReceipt: IReceipt = {
        id: receiptData.id || receiptData._id || receiptId,
        receiptNumber: receiptData.receiptNumber,
        date: receiptData.date,
        amount: receiptData.amount,
        debitAccount: receiptData.debitAccount || { id: '', name: 'غير محدد' },
        creditAccount: receiptData.creditAccount || { id: '', name: 'غير محدد' },
        description: receiptData.description,
        status: receiptData.status,
        createdBy: receiptData.createdBy,
        approvedBy: receiptData.approvedBy,
        approvedAt: receiptData.approvedAt,
        createdAt: receiptData.createdAt,
        customer: receiptData.customer,
        branchId: receiptData.branchId,
        branchName: receiptData.branchName
      };
      
      setReceipt(formattedReceipt);
    } catch (err: any) {
      console.error('خطأ في جلب سند القبض:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء جلب بيانات السند');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (receipt) {
      try {
        await printReceipt(receipt.id);
      } catch (error) {
        console.error('خطأ في طباعة السند:', error);
      }
    }
  };

  const handleEdit = () => {
    if (receipt) {
      onClose();
      navigate(`/finance/receipts/edit/${receipt.id}`);
    }
  };

  const renderStatus = (status: string) => {
    let color: 'success' | 'warning' | 'error' | 'default' = 'default';
    let icon;
    
    switch (status) {
      case 'مؤكد':
        color = 'success';
        icon = <CheckCircleIcon fontSize="small" />;
        break;
      case 'تحت الموافقة':
        color = 'warning';
        break;
      case 'مرفوض':
        color = 'error';
        icon = <CancelIcon fontSize="small" />;
        break;
    }
    
    return (
      <Chip 
        size="medium" 
        label={status}
        color={color}
        icon={icon}
        sx={{ 
          fontWeight: 600,
          fontSize: '0.875rem'
        }}
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: alpha('#30c3b0', 0.05),
        borderBottom: '1px solid',
        borderColor: alpha('#000', 0.08)
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PaidIcon sx={{ mr: 1, color: '#30c3b0', fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              سند قبض
            </Typography>
            {receipt && (
              <Typography variant="body2" color="text.secondary">
                {receipt.receiptNumber}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress size={50} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !receipt ? (
          <Alert severity="warning">السند غير موجود</Alert>
        ) : (
          <Box>
            {/* معلومات السند الأساسية */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                      معلومات السند
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          التاريخ
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(receipt.date).toLocaleDateString('ar-SA', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          الحالة
                        </Typography>
                        {renderStatus(receipt.status)}
                      </Box>
                      
                      {receipt.branchName && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            الفرع
                          </Typography>
                          <Typography variant="body1">
                            {receipt.branchName}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', bgcolor: alpha('#30c3b0', 0.05) }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                      <AttachMoneyIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                      المبلغ
                    </Typography>
                    
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 2 }}>
                      {receipt.amount.toLocaleString('ar-SA')} ر.س
                    </Typography>
                    
                    {receipt.customer && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <PersonIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          العميل
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {receipt.customer}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* تفاصيل الحسابات */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                  <AccountBalanceIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  تفاصيل الحسابات
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.2) }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        الحساب المدين (من)
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {receipt.debitAccount.name}
                      </Typography>
                      {receipt.debitAccount.accountNumber && (
                        <Typography variant="caption" color="text.secondary">
                          رقم الحساب: {receipt.debitAccount.accountNumber}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2) }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        الحساب الدائن (إلى)
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {receipt.creditAccount.name}
                      </Typography>
                      {receipt.creditAccount.accountNumber && (
                        <Typography variant="caption" color="text.secondary">
                          رقم الحساب: {receipt.creditAccount.accountNumber}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* البيان */}
            {receipt.description && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 1, color: '#30c3b0', fontWeight: 600 }}>
                    <DescriptionIcon sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    البيان
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                    {receipt.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* معلومات الإنشاء والاعتماد */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2, color: '#30c3b0', fontWeight: 600 }}>
                  معلومات إضافية
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      أنشئ بواسطة
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {receipt.createdBy}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      تاريخ الإنشاء
                    </Typography>
                    <Typography variant="body2">
                      {new Date(receipt.createdAt).toLocaleString('ar-SA')}
                    </Typography>
                  </Grid>
                  
                  {receipt.approvedBy && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          اعتمد بواسطة
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {receipt.approvedBy}
                        </Typography>
                      </Grid>
                      
                      {receipt.approvedAt && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            تاريخ الاعتماد
                          </Typography>
                          <Typography variant="body2">
                            {new Date(receipt.approvedAt).toLocaleString('ar-SA')}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: alpha('#000', 0.02) }}>
        <Button onClick={onClose} variant="outlined">
          إغلاق
        </Button>
        {receipt && receipt.status !== 'مؤكد' && (
          <Button 
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ color: '#30c3b0', borderColor: '#30c3b0' }}
          >
            تعديل
          </Button>
        )}
        {receipt && (
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#30c3b0',
              '&:hover': { bgcolor: '#25a899' }
            }}
          >
            طباعة
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptView;